// --- FILE: frontend/src/pages/MainAuthPage.tsx ---
// Версия: 3.7.2 (2026-01-23)
// FIX:
// - Убран несуществующий endpoint /auth/verify-code-admin (всегда используем /auth/verify-code)
// - Не ломаем legacy-ответы: token может быть token|accessToken|access_token
// - navigate() только по относительным путям (Router basename=BASE_PATH)
// - Автовход при активной сессии + Enter на всех шагах без деградации исходного flow
// - [v3.7.2] AbortController + mountedRef для предотвращения setState на unmounted компоненте

import { apiUrl, assetUrl } from '../config/paths';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Alert,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  DirectionsBoat,
  Business,
  Shield,
  Email,
  Lock,
  VpnKey,
  Visibility,
  VisibilityOff,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

type Role = 'client' | 'operator' | 'admin';
type AuthMode = 'select' | 'client' | 'operator' | 'admin';

function decodeJWT(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

function roleFromPayload(d: Record<string, unknown> | null): Role {
  if (!d) return 'client';

  const realmAccess = d.realm_access as { roles?: string[] } | undefined;
  if (realmAccess?.roles) {
    if (realmAccess.roles.includes('admin')) return 'admin';
    if (realmAccess.roles.includes('operator')) return 'operator';
    if (realmAccess.roles.includes('client')) return 'client';
  }

  if (d.role && ['client', 'operator', 'admin'].includes(d.role as string)) {
    return d.role as Role;
  }

  return 'client';
}

/**
 * Токен без exp считаем НЕвалидным (не допускаем "вечную сессию").
 * При этом exp должен быть number и finite.
 */
function isTokenValid(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload) return false;

  const exp = (payload as any).exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return false;

  return Date.now() < exp * 1000;
}

function getTargetPathByRole(role: Role): string {
  if (role === 'admin') return '/admin/settings/testing';
  if (role === 'operator') return '/operator';
  return '/client-requests';
}

function extractToken(d: any): string | null {
  // поддерживаем legacy форматы
  return d?.token || d?.accessToken || d?.access_token || d?.accessToken?.token || null;
}

export default function MainAuthPage() {
  const nav = useNavigate();

  // === [v3.7.2] AbortController refs ===
  const mountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [mode, setMode] = useState<AuthMode>('select');
  const [checkingSession, setCheckingSession] = useState(true);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  const [step, setStep] = useState<'email' | 'password' | 'code'>('email');

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [expiresAt, setExpiresAt] = useState<string | undefined>();
  const [countdown, setCountdown] = useState<number | undefined>();

  // === [v3.7.2] Cleanup при размонтировании ===
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Отменяем активный запрос при размонтировании
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Автопроверка активной сессии: один раз при монтировании
  useEffect(() => {
    let cancelled = false;

    const run = () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        if (!isTokenValid(token)) {
          localStorage.removeItem('token');
          localStorage.removeItem('role');
          localStorage.removeItem('email');
          return;
        }

        const tokenRole = roleFromPayload(decodeJWT(token));
        const storedRole = localStorage.getItem('role');
        if (storedRole !== tokenRole) {
          localStorage.setItem('role', tokenRole);
        }

        const targetPath = getTargetPathByRole(tokenRole);
        nav(targetPath, { replace: true });
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        localStorage.removeItem('email');
      } finally {
        if (!cancelled) setCheckingSession(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [nav]);

  // Таймер обратного отсчета срока действия кода
  useEffect(() => {
    if (!expiresAt) return;

    const t = setInterval(() => {
      const now = Date.now();
      const exp = new Date(expiresAt).getTime();
      const diff = Math.max(0, Math.floor((exp - now) / 1000));
      if (mountedRef.current) {
        setCountdown(diff);
      }
    }, 1000);

    return () => clearInterval(t);
  }, [expiresAt]);

  const reset = useCallback(() => {
    setEmail('');
    setPassword('');
    setCode('');
    setErr(null);
    setOk(null);
    setStep('email');
    setExpiresAt(undefined);
    setCountdown(undefined);
  }, []);

  const handleSelect = useCallback(
    (m: AuthMode) => {
      if (m === 'operator') {
        nav('/login/operator');
        return;
      }
      if (m === 'admin') {
        nav('/login/admin');
        return;
      }
      setMode(m);
      setErr(null);
      setOk(null);
      setStep('email');
    },
    [nav]
  );

  const requestCodeClient = useCallback(async () => {
    // [v3.7.2] Отменяем предыдущий запрос если есть
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setErr(null);
    setOk(null);

    try {
      const r = await fetch(apiUrl('/auth/request-code'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
        signal: controller.signal, // [v3.7.2]
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        // 429 rate-limit — show cooldown timer if retryAfterSec provided
        if (r.status === 429 && d?.retryAfterSec && mountedRef.current) {
          setExpiresAt(new Date(Date.now() + d.retryAfterSec * 1000).toISOString());
        }
        throw new Error(d?.message || 'Ошибка отправки кода');
      }

      // [v3.7.2] Проверяем что компонент еще смонтирован
      if (!mountedRef.current) return;

      setOk(d?.message || 'Код отправлен на e-mail');
      setExpiresAt(d?.expiresAt);
      setStep('code');
    } catch (e: unknown) {
      // [v3.7.2] Игнорируем AbortError
      if (e instanceof Error && e.name === 'AbortError') return;
      if (!mountedRef.current) return;
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [email]);

  const requestCodeWithPassword = useCallback(async () => {
    // [v3.7.2] Отменяем предыдущий запрос если есть
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setErr(null);
    setOk(null);

    try {
      const r = await fetch(apiUrl('/auth/request-code-with-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
        }),
        signal: controller.signal, // [v3.7.2]
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (r.status === 429 && d?.retryAfterSec && mountedRef.current) {
          setExpiresAt(new Date(Date.now() + d.retryAfterSec * 1000).toISOString());
        }
        throw new Error(d?.message || 'Ошибка отправки кода');
      }

      // [v3.7.2] Проверяем что компонент еще смонтирован
      if (!mountedRef.current) return;

      setOk(d?.message || 'Код отправлен на e-mail');
      setExpiresAt(d?.expiresAt);
      setStep('code');
    } catch (e: unknown) {
      // [v3.7.2] Игнорируем AbortError
      if (e instanceof Error && e.name === 'AbortError') return;
      if (!mountedRef.current) return;
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [email, password]);

  const verifyCode = useCallback(async () => {
    // [v3.7.2] Отменяем предыдущий запрос если есть
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setErr(null);
    setOk(null);

    try {
      // КРИТИЧНО: единственный корректный endpoint (без verify-code-admin)
      const r = await fetch(apiUrl('/auth/verify-code'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          code: code.trim(),
        }),
        signal: controller.signal, // [v3.7.2]
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || 'Неверный или истекший код');

      const token = extractToken(d);
      if (!token) throw new Error('Токен не получен');

      localStorage.setItem('token', token);

      const payload = decodeJWT(token);
      const role = roleFromPayload(payload);

      localStorage.setItem('role', String(role));
      localStorage.setItem('email', email.trim().toLowerCase());

      // Навигация происходит даже если компонент размонтируется после
      nav(getTargetPathByRole(role), { replace: true });
    } catch (e: unknown) {
      // [v3.7.2] Игнорируем AbortError
      if (e instanceof Error && e.name === 'AbortError') return;
      if (!mountedRef.current) return;
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [email, code, nav]);

  // Оставляем legacy login (если используется где-то в проекте)
  const loginLegacy = useCallback(async () => {
    // [v3.7.2] Отменяем предыдущий запрос если есть
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setErr(null);
    setOk(null);

    try {
      const r = await fetch(apiUrl('/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
        }),
        signal: controller.signal, // [v3.7.2]
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || 'Ошибка входа');

      const token = extractToken(d);
      if (!token) throw new Error('Токен не получен');

      localStorage.setItem('token', token);

      const role = roleFromPayload(decodeJWT(token));
      localStorage.setItem('role', String(role));
      localStorage.setItem('email', email.trim().toLowerCase());

      nav(getTargetPathByRole(role), { replace: true });
    } catch (e: unknown) {
      // [v3.7.2] Игнорируем AbortError
      if (e instanceof Error && e.name === 'AbortError') return;
      if (!mountedRef.current) return;
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [email, password, nav]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      if (loading) return;

      if (step === 'email') {
        if (!email.trim()) return;
        if (mode === 'client') requestCodeClient();
        else setStep('password');
        return;
      }

      if (step === 'password') {
        if (!password.trim()) return;
        requestCodeWithPassword();
        return;
      }

      if (step === 'code') {
        if (!code.trim()) return;
        verifyCode();
      }
    },
    [
      loading,
      step,
      email,
      password,
      code,
      mode,
      requestCodeClient,
      requestCodeWithPassword,
      verifyCode,
    ]
  );

  const steps = useMemo(
    () => (mode === 'client' ? ['E-mail', 'Код'] : ['E-mail', 'Пароль', 'Код']),
    [mode]
  );

  if (checkingSession) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', my: 6, p: 2 }}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <img
          src={assetUrl('/sign_70.png')}
          alt='ФГБУ "Морская спасательная служба"'
          style={{ width: 100, height: 100 }}
        />
      </Box>

      <Typography variant="h4" sx={{ mb: 1, textAlign: 'center', fontWeight: 700 }}>
        Система ССТО — модуль ТЕСТ
      </Typography>
      <Typography variant="subtitle1" sx={{ textAlign: 'center', color: '#666', mb: 0.5 }}>
        Судовая система тревожного оповещения
      </Typography>
      <Typography
        variant="caption"
        sx={{ textAlign: 'center', color: '#999', display: 'block', mb: 3 }}
      >
        ГМСКЦ ФГБУ "Морская спасательная служба"
      </Typography>

      {mode === 'select' ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  <DirectionsBoat sx={{ mr: 1 }} /> ЗАЯВКА на ТЕСТ ССТО
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Вход через код на e-mail (без пароля). Для судовладельцев/уполномоченных лиц.
                </Typography>
              </CardContent>
              <CardActions>
                <Button fullWidth variant="contained" onClick={() => handleSelect('client')}>
                  Войти
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  <Business sx={{ mr: 1 }} /> Опер. дежурный
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  E-mail + пароль + код на e-mail.
                </Typography>
              </CardContent>
              <CardActions>
                <Button fullWidth variant="outlined" onClick={() => handleSelect('operator')}>
                  Войти
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  <Shield sx={{ mr: 1 }} /> Администратор
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  E-mail + пароль + код на e-mail.
                </Typography>
              </CardContent>
              <CardActions>
                <Button fullWidth variant="outlined" onClick={() => handleSelect('admin')}>
                  Войти
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Box>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              setMode('select');
              reset();
            }}
            sx={{ mb: 1 }}
            disabled={loading}
          >
            Назад
          </Button>

          <Stepper
            activeStep={
              mode === 'client'
                ? step === 'email'
                  ? 0
                  : 1
                : step === 'email'
                  ? 0
                  : step === 'password'
                    ? 1
                    : 2
            }
            sx={{ mb: 2 }}
          >
            {steps.map((s, idx) => (
              <Step key={idx}>
                <StepLabel>{s}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {err && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
              {err}
            </Alert>
          )}
          {ok && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk(null)}>
              {ok}
            </Alert>
          )}

          {step === 'email' && (
            <TextField
              fullWidth
              label="E-mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          )}

          {mode !== 'client' && step === 'password' && (
            <>
              <TextField
                fullWidth
                label="Пароль"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPwd((p) => !p)} edge="end">
                        {showPwd ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              {/* Если нужно legacy login по паролю — оставлено и не ломает flow */}
              {/* <Button variant="text" onClick={loginLegacy} disabled={loading || !email || !password}>Войти без кода (legacy)</Button> */}
            </>
          )}

          {step === 'code' && (
            <TextField
              fullWidth
              label="Код (6 цифр)"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ''))}
              onKeyDown={handleKeyDown}
              autoFocus
              inputProps={{ maxLength: 6 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKey />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
          )}

          {countdown !== undefined && (
            <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>
              Срок действия кода: {Math.floor((countdown || 0) / 60)}:
              {((countdown || 0) % 60).toString().padStart(2, '0')}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            {step === 'email' &&
              (mode === 'client' ? (
                <Button
                  disabled={!email || loading}
                  variant="contained"
                  onClick={requestCodeClient}
                >
                  Получить код на e-mail
                </Button>
              ) : (
                <Button
                  disabled={!email || loading}
                  variant="contained"
                  onClick={() => setStep('password')}
                >
                  Далее
                </Button>
              ))}

            {mode !== 'client' && step === 'password' && (
              <Button
                disabled={!email || !password || loading}
                variant="contained"
                onClick={requestCodeWithPassword}
              >
                Отправить код на e-mail
              </Button>
            )}

            {step === 'code' && (
              <Button
                disabled={!email || !code || code.length < 6 || loading}
                variant="contained"
                onClick={verifyCode}
              >
                Войти
              </Button>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 6, pt: 2, borderTop: '1px solid #eee', textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: '#999' }}>
          © {new Date().getFullYear()} ФГБУ "Морская спасательная служба"
          <br />
          Все права защищены
        </Typography>
      </Box>
    </Box>
  );
}
